import { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "tribelog.appearance";
const DAY_THEME_ENABLED = true;

const AppThemeContext = createContext({
  mode: "night",
  resolvedMode: "night",
  setMode: () => {},
  theme: {},
});

const PALETTES = {
  night: {
    appBg: "#080808",
    cardBg: "rgba(255,255,255,0.03)",
    cardBgStrong: "rgba(255,255,255,0.05)",
    surfaceElevated: "#1B1B1B",
    cardBorder: "rgba(255,255,255,0.06)",
    cardBorderStrong: "rgba(255,255,255,0.12)",
    cardShadow: "none",
    cardShadowRaise: "0 18px 50px rgba(0,0,0,0.28)",
    divider: "rgba(255,255,255,0.05)",
    inputBg: "rgba(255,255,255,0.05)",
    inputBorder: "rgba(255,255,255,0.10)",
    overlayBg: "rgba(0,0,0,0.8)",
    progressTrack: "rgba(255,255,255,0.06)",
    heroBg: "linear-gradient(180deg, #0f0f0f 0%, #080808 100%)",
    text: "#FFFFFF",
    textSoft: "#CCCCCC",
    textInverse: "#080808",
    muted: "#555555",
    mutedStrong: "#888888",
    navBg: "rgba(8,8,8,0.95)",
    navInactive: "#444444",
  },
  day: {
    appBg: "#F7F4EF",
    cardBg: "rgba(255,255,255,0.84)",
    cardBgStrong: "rgba(255,255,255,0.96)",
    surfaceElevated: "#FFFFFF",
    cardBorder: "rgba(34,34,34,0.10)",
    cardBorderStrong: "rgba(34,34,34,0.18)",
    cardShadow: "0 10px 30px rgba(32,24,16,0.08)",
    cardShadowRaise: "0 18px 46px rgba(32,24,16,0.13)",
    divider: "rgba(34,34,34,0.08)",
    inputBg: "rgba(255,255,255,0.98)",
    inputBorder: "rgba(34,34,34,0.16)",
    overlayBg: "rgba(23,23,23,0.34)",
    progressTrack: "rgba(34,34,34,0.12)",
    heroBg: "linear-gradient(180deg, #FFF8EF 0%, #F7F4EF 100%)",
    text: "#171717",
    textSoft: "#2F2F2F",
    textInverse: "#FFFFFF",
    muted: "#76716A",
    mutedStrong: "#514B44",
    navBg: "rgba(255,255,255,0.92)",
    navInactive: "#7A746E",
  },
};

function normalizeMode(value) {
  if (!DAY_THEME_ENABLED) return "night";
  return value === "day" ? "day" : "night";
}

function getInitialMode() {
  if (!DAY_THEME_ENABLED || typeof window === "undefined") return "night";
  return normalizeMode(localStorage.getItem(STORAGE_KEY));
}

export function AppThemeProvider({ children }) {
  const [mode, setModeState] = useState(getInitialMode);
  const resolvedMode = normalizeMode(mode);
  const theme = PALETTES[resolvedMode] || PALETTES.night;

  useEffect(() => {
    document.documentElement.dataset.appearance = resolvedMode;
    document.documentElement.dataset.theme = resolvedMode === "day" ? "day" : "night";
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", resolvedMode === "day" ? "#F7F4EF" : "#12C7C1");
  }, [resolvedMode]);

  const value = useMemo(() => ({
    mode,
    resolvedMode,
    setMode: nextMode => {
      const safeMode = normalizeMode(nextMode);
      setModeState(safeMode);
      localStorage.setItem(STORAGE_KEY, safeMode);
    },
    theme,
  }), [mode, resolvedMode, theme]);

  return <AppThemeContext.Provider value={value}>{children}</AppThemeContext.Provider>;
}

export function useAppTheme() {
  return useContext(AppThemeContext);
}
